import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import logger from '../../utils/logger'

import styles from './ErrorBoundary.module.scss'

/**
 * ErrorBoundary component catches errors in child components and displays a fallback UI
 * rather than crashing the entire application
 */
class ErrorBoundaryBase extends Component {
  constructor (props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError (error) {
    return { hasError: true, error }
  }

  componentDidCatch (error, errorInfo) {
    logger.error('Error caught by ErrorBoundary', { error, errorInfo })
    this.setState({ errorInfo })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render () {
    const { t, children, fallback, componentName } = this.props
    const { hasError, error } = this.state

    if (hasError) {
      if (fallback) {
        return fallback
      }

      return (
        <Box
          className={styles.errorContainer}
          data-testid='error-boundary-fallback'
        >
          <Typography variant='h6' className={styles.errorTitle}>
            {t('errors.componentError', { componentName: componentName || t('common.component') })}
          </Typography>
          <Typography variant='body2' className={styles.errorMessage}>
            {error?.message || t('errors.generic')}
          </Typography>
          <Button
            variant='contained'
            color='secondary'
            onClick={this.handleReset}
            className={styles.resetButton}
          >
            {t('errors.tryAgain')}
          </Button>
        </Box>
      )
    }

    return children
  }
}

ErrorBoundaryBase.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  componentName: PropTypes.string,
  t: PropTypes.func.isRequired
}

const ErrorBoundary = (props) => {
  const { t } = useTranslation()
  return <ErrorBoundaryBase t={t} {...props} />
}

export const UIErrorBoundaryView = ErrorBoundary
export default ErrorBoundary
